import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Coins,
  Users,
  Activity,
  TrendingUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AdminLayout from "@/components/layout/AdminLayout";
import { fetchUsageStats, fetchCreditSummary } from "@/api/ai-credits";

const TIME_RANGES = [
  { value: "5", label: "Last 5 hours" },
  { value: "24", label: "Last 24 hours" },
  { value: "72", label: "Last 3 days" },
  { value: "168", label: "Last 7 days" },
  { value: "720", label: "Last 30 days" },
];

const tierColors: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  admin: "default",
  subscriber: "secondary",
  free: "outline",
};

export default function AiUsageAnalytics() {
  const [hours, setHours] = useState("24");
  const hoursNum = parseInt(hours);

  const { data: summary } = useQuery({
    queryKey: ["ai-credit-summary", hoursNum],
    queryFn: () => fetchCreditSummary(hoursNum),
  });

  const { data: usageStats = [], isLoading } = useQuery({
    queryKey: ["ai-usage-stats", hoursNum],
    queryFn: () => fetchUsageStats(hoursNum),
  });

  const formatCredits = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <AdminLayout title="AI Usage Analytics">
      <div className="space-y-6 p-6">
        {/* Time range selector */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Usage Overview</h2>
          <Select value={hours} onValueChange={setHours}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Credits Used
              </CardTitle>
              <Coins className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {summary ? formatCredits(summary.total_credits_used) : "..."}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Unique Users
              </CardTitle>
              <Users className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {summary?.unique_users ?? "..."}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Interactions
              </CardTitle>
              <Activity className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {summary?.total_interactions ?? "..."}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Avg Credits / User
              </CardTitle>
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {summary
                  ? formatCredits(summary.avg_credits_per_user)
                  : "..."}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Per-user usage table */}
        <Card>
          <CardHeader>
            <CardTitle>Usage by User</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : usageStats.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No AI usage in this time range.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead className="text-right">Credits Used</TableHead>
                    <TableHead className="text-right">Interactions</TableHead>
                    <TableHead className="text-right">Avg / Interaction</TableHead>
                    <TableHead>Last Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usageStats.map((stat) => (
                    <TableRow key={stat.user_id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {stat.user_name || "Unknown"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {stat.user_email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={tierColors[stat.tier] || "outline"}>
                          {stat.tier}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCredits(stat.total_credits_used)}
                      </TableCell>
                      <TableCell className="text-right">
                        {stat.interaction_count}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {stat.interaction_count > 0
                          ? formatCredits(
                              Math.round(
                                stat.total_credits_used /
                                  stat.interaction_count
                              )
                            )
                          : "0"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatTime(stat.last_active)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
